import { TableColumnType } from "antd"
import { ColumnsType, TablePaginationConfig } from "antd/lib/table"
import { SorterResult } from "antd/lib/table/interface"
import React, { useCallback, useMemo } from "react"
import { generatePath, useHistory } from "react-router-dom"
import { GenerateColumnsProps } from "~Features/pages/purchaseOrders/types/utils/helper.util"
import { useLocalStorage } from "~Shared/hooks/useLocalStorage"
import { LocalStorageService } from "~Shared/services/localstorage.service"

export interface HeaderCellProps {
    children: React.ReactNode
    className?: string
    style?: React.CSSProperties
}

export interface UseTablePropsBase<T> {
    data: Array<T>
    generateColumns: ({ updateRecord, deleteRecord, getRecord }: GenerateColumnsProps<T>) => Array<TableColumnType<T>>
    deleteFn?: (key: string) => void
    getItemFn?: (key: string) => T | undefined
    basePath?: string
    // Add custom prop mapping
    columnFunctions?: GenerateColumnsProps<T>
    // Add pagination support
    enablePagination?: boolean
    totalItems: number
    currentPage: number
    setCurrentPage: (page: number) => void
    pageSize: number
    setPageSize: (size: number) => void
    setSorting?: (sorting: { field?: string; order?: SorterResult<T>["order"] }) => void
}

interface UseTableProps<T> extends UseTablePropsBase<T> {
    localStorageKeyVisibleColumns: string
    localStorageKeyColumnOrder: string
    columnToKeepAtEnd: string
}

export const useTable = <T extends { key: string }>({
    data,
    generateColumns,
    deleteFn,
    getItemFn,
    basePath,
    columnFunctions = {} as GenerateColumnsProps<T>,
    enablePagination = false,
    totalItems = 0,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    localStorageKeyVisibleColumns,
    localStorageKeyColumnOrder,
    columnToKeepAtEnd,
    setSorting,
}: UseTableProps<T>) => {
    const history = useHistory()

    // Navigation handler
    const handleViewItem = useCallback(
        (item: T) => {
            if (basePath) {
                history.push(generatePath(basePath, { id: item.key }))
            }
        },
        [history, basePath],
    )

    // Delete handler
    const handleDeleteItem = useCallback(
        (key: string) => {
            if (deleteFn) {
                deleteFn(key)
            }
        },
        [deleteFn],
    )

    // Edit handler (returns the item to be edited)
    const handleEditItem = useCallback(
        (item: T) => {
            if (getItemFn) {
                return getItemFn(item.key)
            }
            return item
        },
        [getItemFn],
    )

    // Generate columns with handlers - use custom prop names
    const allColumns = useMemo(
        () =>
            generateColumns({
                updateRecord: columnFunctions.updateRecord,
                deleteRecord: columnFunctions.deleteRecord,
                getRecord: handleViewItem,
                t: columnFunctions.t,
            }),
        [handleViewItem, columnFunctions],
    )

    // Load initial state from localStorage
    const getInitialVisibleColumns = (): Array<string> => {
        try {
            const stored = JSON.parse(LocalStorageService.get(localStorageKeyVisibleColumns) ?? "[]")
            if (stored) {
                // Validate that all stored columns still exist
                const validColumns = stored.filter((col: string) => allColumns.some((column) => column.key === col))
                return validColumns.length > 0 ? validColumns : allColumns.map((col) => col.key as string)
            }
        } catch (error) {
            console.warn("Failed to load visible columns from localStorage:", error)
        }
        return allColumns.map((col) => col.key as string)
    }

    const getInitialColumnOrder = (): Array<string> => {
        try {
            const stored = JSON.parse(LocalStorageService.get(localStorageKeyColumnOrder) ?? "[]")
            if (stored) {
                // Validate that all stored columns still exist
                const validColumns = stored.filter((col: string) => allColumns.some((column) => column.key === col))
                return validColumns.length > 0 ? validColumns : allColumns.map((col) => col.key as string)
            }
        } catch (error) {
            console.warn("Failed to load column order from localStorage:", error)
        }
        return allColumns.map((col) => col.key as string)
    }

    const [visibleColumns, setVisibleColumns] = useLocalStorage<Array<string>>(localStorageKeyVisibleColumns, getInitialVisibleColumns())

    const [columnOrder, setColumnOrder] = useLocalStorage<Array<string>>(localStorageKeyColumnOrder, getInitialColumnOrder())

    // Filter and order visible columns
    const columns = useMemo(() => {
        const orderedColumns = columnOrder.map((key) => allColumns.find((col) => col.key === key)).filter(Boolean)

        return orderedColumns.filter((col) => visibleColumns.includes(col!.key as string))
    }, [allColumns, visibleColumns, columnOrder])

    // Column visibility handlers

    const handleToggleColumn = useCallback(
        (key: string) => {
            setVisibleColumns((prev) => {
                if (prev.includes(key)) {
                    // Column is being hidden - remove it from visible columns
                    return prev.filter((col) => col !== key)
                }
                // Column is being shown - add it to visible columns
                return [...prev, key]
            })

            // Also update column order to move hidden columns to the end, but keep action column last
            setColumnOrder((prev) => {
                const isCurrentlyVisible = visibleColumns.includes(key)

                if (isCurrentlyVisible) {
                    // Column is being hidden - move it to invisible section (before action)
                    const withoutColumn = prev.filter((col) => col !== key)
                    const visibleCols = withoutColumn.filter((col) => visibleColumns.includes(col) && col !== columnToKeepAtEnd)
                    const invisibleCols = withoutColumn.filter((col) => !visibleColumns.includes(col) && col !== columnToKeepAtEnd)
                    const actionCol = withoutColumn.filter((col) => col === columnToKeepAtEnd)

                    return [...visibleCols, ...invisibleCols, key, ...actionCol]
                }
                // Column is being shown - move it to visible section
                const withoutColumn = prev.filter((col) => col !== key)
                const visibleCols = withoutColumn.filter((col) => visibleColumns.includes(col) && col !== columnToKeepAtEnd)
                const invisibleCols = withoutColumn.filter((col) => !visibleColumns.includes(col) && col !== columnToKeepAtEnd)
                const actionCol = withoutColumn.filter((col) => col === columnToKeepAtEnd)

                return [...visibleCols, key, ...invisibleCols, ...actionCol]
            })
        },
        [visibleColumns],
    )

    const handleShowAllColumns = useCallback(() => {
        setVisibleColumns(allColumns.map((col) => col.key as string))
    }, [allColumns])

    const handleHideAllColumns = useCallback(() => {
        // Keep at least the first column visible (usually name/identifier)
        setVisibleColumns([allColumns[0]?.key as string].filter(Boolean))
    }, [allColumns])

    // Table row click handler
    const getRowProps = useCallback(
        (record: T) => ({
            onClick: () => handleViewItem(record),
            style: { cursor: "pointer" },
        }),
        [handleViewItem],
    )

    // Column reorder handler
    const handleReorderColumns = useCallback((newOrder: Array<string>) => {
        setColumnOrder(newOrder)
    }, [])

    // Table change handler (pagination and sorting)
    const handleTableChange = useCallback(
        (pagination: TablePaginationConfig, _filters: any, sorter: SorterResult<T>) => {
            // Handle pagination
            if (pagination.current !== undefined && pagination.pageSize !== undefined) {
                setCurrentPage(pagination.current)
                setPageSize(pagination.pageSize)
            }

            if (sorter && setSorting) {
                const field = sorter.columnKey?.toString()
                const { order } = sorter
                setSorting({ field, order })
            }
        },
        [setCurrentPage, setPageSize],
    )

    // Create pagination config
    const paginationConfig: TablePaginationConfig | false = enablePagination
        ? {
              current: currentPage,
              pageSize,
              total: totalItems,
              showSizeChanger: true,
              showQuickJumper: false,
              pageSizeOptions: ["10", "20", "50", "100"],
          }
        : false

    return {
        // Data
        tableData: data,
        columns,
        allColumns,
        visibleColumns,
        columnOrder,

        handleToggleColumn,
        handleShowAllColumns,
        handleHideAllColumns,
        handleReorderColumns,

        // Row handlers
        handleViewItem,
        handleEditItem,
        handleDeleteItem,
        getRowProps,

        // Table props (ready to spread into Table component)
        tableProps: {
            columns: columns as ColumnsType<T>,
            dataSource: data,
            pagination: paginationConfig,
            scroll: { x: "fit-content" },
            className: "custom-table",
            onRow: getRowProps,
            onChange: handleTableChange,
            components: {
                header: {
                    cell: ({ children, ...props }: HeaderCellProps) => <th {...props}>{children}</th>,
                },
            },
        },
    }
}
